/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Reusable table UX: synced top+bottom horizontal scrollbar + client-side pagination.
 * Shared across operations dashboards (Platform Health, Network Topology, ...).
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';

/** Wraps a wide table with a horizontal scrollbar on BOTH the top and bottom,
 *  kept in sync. The top bar mirrors the table's scrollWidth via a spacer. */
export const DualScrollTable: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const topRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);
  const lock = useRef<'top' | 'body' | null>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      setContentWidth(el.scrollWidth);
      // >2px threshold avoids phantom scrollbars from sub-pixel rounding.
      setOverflowing(el.scrollWidth - el.clientWidth > 2);
    };
    measure();
    // ResizeObserver fires when the container resizes (viewport change, sidebar toggle).
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // MutationObserver fires when rows load asynchronously after mount — without
    // this, server-paginated tables that load data after render never detect overflow.
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, []);

  const onTop = () => {
    if (lock.current === 'body') { lock.current = null; return; }
    if (topRef.current && bodyRef.current) {
      lock.current = 'top';
      bodyRef.current.scrollLeft = topRef.current.scrollLeft;
    }
  };
  const onBody = () => {
    if (lock.current === 'top') { lock.current = null; return; }
    if (topRef.current && bodyRef.current) {
      lock.current = 'body';
      topRef.current.scrollLeft = bodyRef.current.scrollLeft;
    }
  };

  return (
    <>
      {/* Mirror top scrollbar — rendered only when the table actually overflows,
          so a table that fits the viewport shows no scrollbar and no empty strip.
          The body keeps overflow-x-auto, so its native bottom scrollbar likewise
          appears only on genuine overflow (e.g. a wide table on a narrow screen). */}
      {overflowing && (
        <div ref={topRef} onScroll={onTop} className="overflow-x-auto overflow-y-hidden">
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}
      <div ref={bodyRef} onScroll={onBody} className="dual-scroll-body overflow-x-auto">
        {children}
      </div>
    </>
  );
};

export const PAGE_SIZES = [10, 25, 50, 100] as const;

/** Client-side pagination over an in-memory list. safePage clamps so a shrinking
 *  list never lands on an out-of-range page. changeSize resets to page 1. */
export function usePagination<T>(items: T[], initialSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(initialSize);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = useMemo(() => items.slice(start, start + pageSize), [items, start, pageSize]);
  const changeSize = (s: number) => { setPageSize(s); setPage(1); };
  return { page: safePage, setPage, pageSize, changeSize, total, totalPages, pageItems, start };
}

export const PaginationControls: React.FC<{
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  start: number;
  shown: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}> = ({ page, pageSize, total, totalPages, start, shown, onPage, onPageSize }) => {
  // No rows → nothing to render.
  if (total === 0) return null;
  const btn = 'px-2 py-1 rounded text-[var(--color-text-secondary)] disabled:opacity-40 enabled:hover:bg-[var(--color-bg-subtle)]';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[var(--color-border)] text-sm text-[var(--color-text-secondary)]">
      {/* Page size selector — always visible so users can change density even when
          all rows already fit on one page (e.g. server-paginated tables). */}
      <div className="flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="border border-[var(--color-border)] rounded-md bg-[var(--color-bg-card)] px-2 py-1 text-sm"
        >
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <span>
        {shown === total
          ? `${total} row${total !== 1 ? 's' : ''}`
          : `Showing ${start + 1}–${start + shown} of ${total}`}
      </span>
      {/* Navigation only when there is more than one page to navigate. */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button className={btn} onClick={() => onPage(1)} disabled={page <= 1}>«</button>
          <button className={btn} onClick={() => onPage(page - 1)} disabled={page <= 1}>‹</button>
          <span className="px-2">Page {page} of {totalPages}</span>
          <button className={btn} onClick={() => onPage(page + 1)} disabled={page >= totalPages}>›</button>
          <button className={btn} onClick={() => onPage(totalPages)} disabled={page >= totalPages}>»</button>
        </div>
      )}
    </div>
  );
};
